import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import type { Actions } from "./actions";
import { useLabels } from "./labels";
import { useBoardUI } from "./store";

export const QUICK_EMOJI = ["👍", "❤️", "🔥", "✅", "👀", "😂", "🎉", "❓", "👎", "⭐"];

/** Floating pickers opened from the context menu: labels and reactions. Positioned in screen space over the viewport. */
export function Pickers({ actions, canEdit }: { actions: Actions; canEdit: boolean }) {
  const picker = useBoardUI((s) => s.picker);
  const close = () => useBoardUI.getState().setPicker(null);
  useEffect(() => {
    if (!picker) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    const onDown = (e: PointerEvent) => {
      if (!(e.target as HTMLElement).closest("[data-picker]")) close();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onDown, { capture: true });
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onDown, { capture: true });
    };
  }, [picker]);
  if (!picker || !canEdit) return null;
  return (
    <div
      data-picker
      className="absolute z-40 rounded-md border border-border bg-panel p-2 shadow-panel"
      style={{ left: picker.sx, top: picker.sy }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {picker.kind === "labels" ? (
        <LabelPicker actions={actions} />
      ) : picker.kind === "alias" ? (
        <AliasPicker
          onPick={(boardId) => {
            void actions.create("alias", picker.px ?? 0, picker.py ?? 0, { targetBoardId: boardId });
            close();
          }}
        />
      ) : (
        <ReactionPicker actions={actions} onDone={close} />
      )}
    </div>
  );
}

function LabelPicker({ actions }: { actions: Actions }) {
  const t = useT();
  const { labels, loaded, load, create } = useLabels();
  const [q, setQ] = useState("");
  const [, bump] = useState(0);
  useEffect(() => {
    if (!loaded) void load();
  }, [loaded, load]);
  const ids = actions.selectedIds();
  const current = new Set(
    ids.flatMap((id) => (actions.cards.get(id)?.get("labels") as string[] | undefined) ?? []),
  );
  const filtered = labels.filter((l) => l.name.toLowerCase().includes(q.toLowerCase()));
  const exact = labels.some((l) => l.name.toLowerCase() === q.trim().toLowerCase());
  const toggle = (id: string) => {
    actions.toggleLabel(id);
    bump((n) => n + 1);
  };
  return (
    <div className="w-56">
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={async (e) => {
          if (e.key === "Enter" && q.trim()) {
            const l = filtered[0] && exact ? filtered[0] : await create(q.trim());
            toggle(l.id);
            setQ("");
          }
        }}
        placeholder={t.searchLabels}
        className="mb-2 w-full rounded-sm border border-border bg-panel-2 px-2 py-1 text-[13px] outline-none"
      />
      <ul className="max-h-56 overflow-auto">
        {filtered.map((l) => (
          <li key={l.id}>
            <button
              type="button"
              onClick={() => toggle(l.id)}
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1 text-left text-[13px] hover:bg-panel-2"
            >
              <span className="size-3 rounded-full" style={{ background: l.color }} />
              <span className="flex-1 truncate">{l.name}</span>
              {current.has(l.id) ? <span>✓</span> : null}
            </button>
          </li>
        ))}
        {q.trim() && !exact ? (
          <li>
            <button
              type="button"
              onClick={async () => toggle((await create(q.trim())).id)}
              className="w-full rounded-sm px-2 py-1 text-left text-[13px] text-muted hover:bg-panel-2"
            >
              {t.createLabel(q.trim())}
            </button>
          </li>
        ) : null}
      </ul>
    </div>
  );
}

function ReactionPicker({ actions, onDone }: { actions: Actions; onDone: () => void }) {
  return (
    <div className="flex gap-1">
      {QUICK_EMOJI.map((e) => (
        <button
          key={e}
          type="button"
          onClick={() => (actions.toggleReaction(e), onDone())}
          className="size-8 rounded-sm text-lg hover:bg-panel-2"
        >
          {e}
        </button>
      ))}
    </div>
  );
}

/** Search boards by title to place a shortcut tile. */
function AliasPicker({ onPick }: { onPick: (boardId: string) => void }) {
  const t = useT();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<{ id: string; title: string; parentTitle: string | null }[]>([]);
  useEffect(() => {
    if (q.trim().length < 2) return setRows([]);
    let alive = true;
    const id = setTimeout(() => {
      api<{ boards: typeof rows }>(`/api/search?q=${encodeURIComponent(q.trim())}`)
        .then((r) => alive && setRows(r.boards))
        .catch(() => {});
    }, 150);
    return () => {
      alive = false;
      clearTimeout(id);
    };
  }, [q]);
  return (
    <div className="w-72">
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={t.aliasSearch}
        className="mb-1 w-full rounded-sm border border-border bg-panel-2 px-2 py-1 text-sm text-text outline-none"
      />
      <div className="max-h-60 overflow-y-auto">
        {rows.map((b) => (
          <button
            key={b.id}
            type="button"
            onClick={() => onPick(b.id)}
            className="block w-full rounded-sm px-2 py-1 text-left text-sm hover:bg-panel-2"
          >
            ▣ {b.title}
            {b.parentTitle ? <span className="ml-1 text-xs text-dim">· {b.parentTitle}</span> : null}
          </button>
        ))}
        {q.trim().length >= 2 && rows.length === 0 ? (
          <div className="px-2 py-1 text-xs text-dim">{t.searchNoResults}</div>
        ) : null}
      </div>
    </div>
  );
}
